import { useCallback, useEffect, useState } from 'react'

type PageState = 'active' | 'passive' | 'hidden' | 'frozen' | 'terminated'

function getPageState() {
  if (document.visibilityState === 'hidden') {
    return 'hidden'
  }
  if (document.hasFocus()) {
    return 'active'
  }
  return 'passive'
}

const opts = { capture: true }

const otherEvents = ['pageshow', 'focus', 'blur', 'visibilitychange', 'resume']

export function usePageState() {
  // See-also: https://developer.chrome.com/docs/web-platform/page-lifecycle-api#how_to_observe_page_lifecycle_states_in_code
  const [state, setState] = useState<PageState>(getPageState())

  const handleFreeze = useCallback(() => {
    setState('frozen')
  }, [setState])

  const handlePageHide = useCallback(
    (event: PageTransitionEvent) => {
      setState(event.persisted ? 'frozen' : 'terminated')
    },
    [setState]
  )

  const handleOther = useCallback(() => {
    setState(getPageState())
  }, [setState])

  useEffect(() => {
    window.addEventListener('freeze', handleFreeze, opts)
    window.addEventListener('pagehide', handlePageHide, opts)
    otherEvents.forEach((event) => {
      window.addEventListener(event, handleOther, opts)
    })

    return () => {
      window.removeEventListener('freeze', handleFreeze, opts)
      window.removeEventListener('pagehide', handlePageHide, opts)
      otherEvents.forEach((event) => {
        window.removeEventListener(event, handleOther, opts)
      })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return state
}
